/**
 * SegmentTile — full-card tile for one of the 24 segments on the
 * Explore page.
 *
 * Fixes applied (2026-04-29):
 *   - name + tagline now come from the SEGMENTS data object directly.
 *     The original `t('segments:music.name')` calls resolved to empty
 *     strings / raw keys because the segments i18n JSON wraps values
 *     under a nested `"segments"` key, making the cross-namespace
 *     path wrong. `segment.name` and `segment.tagline` are already
 *     correctly cased English strings in the SEGMENTS array.
 *   - Clicking a ready tile now opens the website segment page
 *     (`growing-intelligence.com/<slug>`) which shows the five
 *     starter recipes for that domain. The old destination
 *     (`/marketplace?seg=…`) redirected to `/explore` with no
 *     filtering, so every tile appeared to land on Citizen Mirror
 *     (the first visible card).
 *
 * Per [docs/DASHBOARD-HOME-EXPLORE-IMPLEMENTATION.md §3.3].
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

import type { Segment } from '@/data/segments';
import { arrive, lift } from '@/lib/motion';
import { cn } from '@/utils/cn';

export function SegmentTile({ segment }: { segment: Segment }) {
  const { t } = useTranslation('marketplace');
  const reduce = useReducedMotion();
  const Icon = segment.Icon;
  const ready = segment.status === 'ready';
  const starterCount = segment.starters?.length ?? 0;

  const Body = (
    <article
      className={cn(
        'group flex h-full flex-col rounded-xl border border-border bg-surface p-5 shadow-subtle',
        'transition-shadow duration-150 ease-emphasized',
        ready && 'hover:shadow-raised hover:border-border-strong',
        !ready && 'opacity-60',
      )}
    >
      <div className="flex items-start justify-between">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-spark-soft text-spark"
          aria-hidden
        >
          <Icon className="h-5 w-5" />
        </span>
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
            ready
              ? 'bg-success/10 text-success'
              : 'bg-ink-muted/10 text-ink-muted',
          )}
        >
          {ready ? t('common.ready') : t('common.coming_soon')}
        </span>
      </div>

      {/* segments.json wraps values under a nested "segments" key, so
          the correct i18n path is "segments:segments.<id>.name", not
          "segments:<id>.name". The "description" field carries the tagline. */}
      <h3 className="mt-4 text-base font-semibold tracking-tight text-ink">
        {t(`segments:segments.${segment.id}.name`, { defaultValue: segment.id })}
      </h3>
      <p className="mt-1 line-clamp-2 text-sm text-ink-muted">
        {t(`segments:segments.${segment.id}.description`, { defaultValue: '' })}
      </p>
      <p className="mt-auto pt-4 text-xs font-medium uppercase tracking-wide text-ink-subtle">
        {t('explore.starter_count', { count: starterCount })}
      </p>
    </article>
  );

  if (!ready) return <div>{Body}</div>;

  return (
    <motion.div {...(reduce ? {} : { variants: arrive, whileHover: lift })}>
      {/* Route in-app to Explore filtered by this segment so the
          user stays in the dashboard. Each segment gets a unique
          `?seg=` param which ExplorePage uses to scroll/filter. */}
      <Link to={`/explore?seg=${segment.id}`}>
        {Body}
      </Link>
    </motion.div>
  );
}
